import { useCallback } from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Form, FormTextInput } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import type { RenameBillingGroupFormValues, RenameBillingGroupModalProps } from "./types";

const INITIAL_VALUES: RenameBillingGroupFormValues = {
  newBillingGroupName: "",
};

export const RenameBillingGroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  currentBillingGroupName,
  selectedGroupCount,
}: RenameBillingGroupModalProps) => {
  const handleSubmit = useCallback(
    async (values: RenameBillingGroupFormValues) => {
      const trimmed = values.newBillingGroupName.trim();
      if (!trimmed || trimmed === currentBillingGroupName) {
        return;
      }
      await onSubmit(trimmed);
    },
    [onSubmit, currentBillingGroupName],
  );

  if (!isOpen) {
    return null;
  }

  const groupLabel = selectedGroupCount === 1 ? "billing group" : "billing groups";

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title="Rename billing group"
      description={`${selectedGroupCount} selected ${groupLabel}`}
      data-testid="rename-billing-group-modal"
    >
      <Form<RenameBillingGroupFormValues>
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Current billing group name
              </Text>
              <Text variant="regular-md" color={Colors.Neutral900}>
                {currentBillingGroupName}
              </Text>
            </Flex>
            <FormTextInput
              name="newBillingGroupName"
              label="New billing group name"
              data-testid="new-billing-group-name-input"
              isFullWidth
            />
          </Flex>
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Submit for approval
            </Button>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
